import { createSlice, AnyAction } from '@reduxjs/toolkit';
import { CompanyRo, DemoSourceRo, LoginRo, UserLoginRo } from '../services/api.generated';
import { api } from '../services/api';
import { readJson } from 'src/utils/safeStorage';
import type { DemoAuthUser } from 'src/components/hooks/useDemoAuthBootstrap';

import {
  AUTH_TOKEN_STORAGE_KEY,
  AUTH_USER_STORAGE_KEY,
  isStoredUser,
} from './authSlice.keys';

export { AUTH_TOKEN_STORAGE_KEY, AUTH_USER_STORAGE_KEY };

type authSliceType = {
  user: UserLoginRo | null;
  token: LoginRo['token'];
  isAdmin: boolean;
  isManager: boolean;
  isActive: boolean;
  company: CompanyRo | null;
  /**
   * Source labels of the demo user's counted upload (profile `demoSource`),
   * sent with the anonymous editor events. Memory only: never written to
   * localStorage or any other browser storage.
   */
  demoSource: DemoSourceRo | null;
};

const isAdmin = (user: UserLoginRo) =>
  !!user.roleNames.find((item) => item.toLocaleLowerCase() === 'admin');

const isManager = (user: UserLoginRo) => !!user.isManager;

const isActive = (user: UserLoginRo) => !!user.isActive;

const initialState = () => {
  // A damaged saved user starts logged out: both keys go, so state is consistent.
  const hasStoredUser = localStorage.getItem(AUTH_USER_STORAGE_KEY) !== null;
  const userLocalInfo = readJson<UserLoginRo | null>(AUTH_USER_STORAGE_KEY, null, isStoredUser);
  if (hasStoredUser && !userLocalInfo) localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  const localStorageUserToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  let result: authSliceType = {
    user: null,
    token: '',
    isAdmin: false,
    isManager: false,
    isActive: true,
    company: null,
    demoSource: null,
  };
  if (userLocalInfo && localStorageUserToken) {
    const userLocalToken: LoginRo['token'] = localStorageUserToken;
    result = {
      user: userLocalInfo,
      token: userLocalToken,
      isAdmin: isAdmin(userLocalInfo),
      isManager: isManager(userLocalInfo),
      isActive: isActive(userLocalInfo),
      company: null,
      demoSource: null,
    };
  }
  return result;
};

const isAuthenticated = (action: AnyAction) => {
  return api.endpoints.authControllerLogin.matchFulfilled(action);
};

const authSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    bootstrapAuth: (
      state,
      { payload: { token, user } }: { payload: Pick<LoginRo, 'token' | 'user'> }
    ) => {
      state.user = user;
      state.token = token;
      state.demoSource = null;
      state.isAdmin = isAdmin(user);
      state.isManager = isManager(user);
      state.isActive = isActive(user);
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
    },
    /** Another tab changed the stored session: follow it, do not write back. */
    syncFromStorage: () => initialState(),
    logout: () => {
      localStorage.removeItem(AUTH_USER_STORAGE_KEY);
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      return initialState();
    },
    completePasswordSetup: (state) => {
      if (!state.user) return;
      state.user.requiresPasswordSetup = false;
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(state.user));
    },
  },
  extraReducers: ({ addMatcher }) => {
    addMatcher(isAuthenticated, (state, { payload }: { payload: LoginRo }) => {
      const { token, user } = payload;
      state.user = user;
      state.token = token;
      state.demoSource = null;
      state.isAdmin = !!user.roleNames.find(
        (item) => item.toLocaleLowerCase() === 'admin'
      );
      state.isManager = !!user.isManager;
      state.isActive = !!user.isActive;
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
      localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
    });
    // The login and handoff answers do not say whether the account is a demo
    // account; the profile does. Keep that flag on the session user, or
    // useIsDemoUser stays false after the editor handoff.
    addMatcher(
      api.endpoints.usersControllerGetUserProfile.matchFulfilled,
      (state, { payload }) => {
        const profile = payload as Partial<DemoAuthUser>;
        if (!state.user) return;
        // A late profile of another session must not mark this one.
        if (profile.username !== state.user.username) return;
        if (profile.accountTier !== 'DEMO' && !profile.demo) return;
        const { demoSource } = payload as { demoSource?: DemoSourceRo };
        state.demoSource = demoSource
          ? {
              src: demoSource.src,
              med: demoSource.med,
              cmp: demoSource.cmp,
              ref: demoSource.ref,
              gclid: demoSource.gclid === true,
            }
          : null;
        const user = {
          ...state.user,
          accountTier: profile.accountTier,
          demo: profile.demo,
        } as DemoAuthUser;
        state.user = user;
        localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
      }
    );
    addMatcher(
      api.endpoints.usersControllerFindCompanyUsers.matchFulfilled,
      (state, { payload }) => {
        state.company = payload[0].company || null;
      }
    );
    addMatcher(
      api.endpoints.usersControllerUpdateUserProfile.matchFulfilled,
      (state, { meta }) => {
        localStorage.setItem(
          AUTH_USER_STORAGE_KEY,
          JSON.stringify({
            ...state.user,
            ...meta?.arg?.originalArgs?.userUpdateDto,
          })
        );
        state.user = {
          ...state.user!,
          ...meta?.arg?.originalArgs?.userUpdateDto,
        };
      }
    );
  },
});

export const {
  bootstrapAuth: bootstrapAuthAction,
  logout: logoutAction,
  syncFromStorage: syncAuthFromStorageAction,
  completePasswordSetup: completePasswordSetupAction,
} = authSlice.actions;

export default authSlice.reducer;
